import {
  Component,
  ElementRef,
  ViewChild,
  inject,
  signal,
  computed,
  effect,
  untracked,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, UntypedFormGroup, UntypedFormControl } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { RcModalComponent } from '../../../shared/components/rc-modal/rc-modal.component';
import { InputComponent } from '../../../shared/components/input/input.component';
import { ModalService } from '../../../shared/services/modal.service';
import { DailyDeclarationService } from '../../services/daily-declaration.service';
import { DeclarationMensuelleService } from '../../services/declaration-mensuelle.service';
import { AuthService } from '../../../authentication/services/auth.service';
import { ApiService } from '../../../shared/services/api.service';
import { ToastrService } from 'ngx-toastr';
import { ActivityPickerComponent } from '../activity-picker/activity-picker.component';
import { ACTIVITY_TYPES, ActivityType } from '../../models/activity-type';

type ActivityPeriod = 'morning' | 'afternoon';

interface StoredDayDeclaration {
    date: string;
    morning?: string;
    afternoon?: string;
    ticketRestaurant?: boolean;
    comment?: string;
    managerValidation?: boolean;
    managerComment?: string;
    validated?: boolean;
}

interface MonthlyDeclaration {
    id: number;
    mois?: string;
    declaration: StoredDayDeclaration[];
}

interface WeekDayDeclaration {
    date: string;
    label: string;
    morning: string;
    afternoon: string;
    ticketRestaurant: boolean;
    comment: string;
}

@Component({
    selector: 'app-daily-declaration-modal',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        RcModalComponent,
        InputComponent,
        ActivityPickerComponent,
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './daily-declaration-modal.component.html'
})
export class DailyDeclarationModalComponent {
    protected modalService = inject(ModalService);
    private dailyDeclarationService = inject(DailyDeclarationService);
    private declarationMensuelleService = inject(DeclarationMensuelleService);
    private authService = inject(AuthService);
    private apiService = inject(ApiService);
    private toastrService = inject(ToastrService);

    // Modal ID
    readonly modalId = 'daily-declaration-modal';

    readonly viewMode = signal<'day' | 'week'>('day');
    readonly isSaving = signal<boolean>(false);
    commentForm = new UntypedFormGroup({ comment: new UntypedFormControl('') });

    @ViewChild('weekScroll') private weekScrollRef?: ElementRef<HTMLElement>;

    // Défile d'une carte entière (largeur réelle + espacement), pas d'un montant fixe :
    // un scroll partiel entrait en conflit avec le snap et donnait l'impression que les flèches ne faisaient rien.
    scrollWeek(direction: 1 | -1): void {
        const container = this.weekScrollRef?.nativeElement;
        const card = container?.querySelector('section') as HTMLElement | null;
        if (!container || !card) return;

        const gap = parseFloat(getComputedStyle(container).columnGap || '12') || 12;
        container.scrollBy({ left: direction * (card.offsetWidth + gap), behavior: 'smooth' });
    }

    // ===== Vue semaine (jours de la semaine ouvrée chargés en une fois, jour courant = "vue jour") =====
    readonly weekDays = signal<WeekDayDeclaration[]>([]);
    readonly isLoadingWeek = signal(false);
    private declarationByMonth = new Map<string, MonthlyDeclaration>();
    private weekLoadSequence = 0;

    constructor() {
        effect(() => {
            const result = this.dailyDeclarationService.checkResult();
            if (!result?.required || !result.today) return;

            untracked(() => {
                this.viewMode.set('day');
                void this.loadWeek(result.today);
            });
        });
    }

    // Computed signals
    ticketsRestaurantEnabled = computed(() => {
        return this.dailyDeclarationService.checkResult()?.tickets_restaurant_enabled ?? false;
    });

    todayDate = computed(() => {
        const result = this.dailyDeclarationService.checkResult();
        if (result?.today) {
            return this.parseDate(result.today);
        }
        return new Date();
    });

    todayFormatted = computed(() => {
        const date = this.todayDate();
        const options: Intl.DateTimeFormatOptions = {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        };
        return date.toLocaleDateString('fr-FR', options);
    });

    readonly currentDay = computed(() => {
        const today = this.dailyDeclarationService.checkResult()?.today;
        return this.weekDays().find(day => day.date === today) ?? null;
    });

    canSubmit = computed(() => {
        if (this.isSaving() || this.isLoadingWeek()) return false;
        const day = this.currentDay();
        return !!day?.morning && !!day?.afternoon;
    });

    // Check if ticket restaurant should be shown based on activities
    shouldShowTicketRestaurant = computed(() => {
        if (!this.ticketsRestaurantEnabled()) return false;
        const day = this.currentDay();
        if (!day) return false;
        const workActivities = ['sur-site', 'teletravail', 'formation', 'rdv-client'];
        return workActivities.includes(day.morning) && workActivities.includes(day.afternoon);
    });

    readonly completedDaysCount = computed(() =>
        this.weekDays().filter(day => !!day.morning && !!day.afternoon).length
    );

    // Jours de la semaine (hors aujourd'hui) pas encore complétés : sert à inciter à
    // remplir toute la semaine une fois la déclaration du jour terminée.
    readonly remainingIncompleteDays = computed(() => {
        const today = this.dailyDeclarationService.checkResult()?.today;
        return this.weekDays().filter(day => day.date !== today && !(day.morning && day.afternoon)).length;
    });

    readonly showWeekNudge = computed(() =>
        this.viewMode() === 'day' && this.canSubmit() && this.remainingIncompleteDays() > 0
    );

    // Activity types (liste complète, partagée avec le tableau mensuel)
    readonly activityTypes: ActivityType[] = ACTIVITY_TYPES;

    setActivity(period: ActivityPeriod, activity: string): void {
        const today = this.dailyDeclarationService.checkResult()?.today;
        if (!today) return;
        this.setActivityForDate(today, period, activity);
    }

    setActivityForDate(date: string, period: ActivityPeriod, activity: string): void {
        this.weekDays.update(days => days.map(day => {
            if (day.date !== date) return day;

            const updated = { ...day, [period]: activity };
            return {
                ...updated,
                ticketRestaurant: this.shouldReceiveTicketRestaurant(updated),
            };
        }));
    }

    onTicketRestaurantChange(checked: boolean): void {
        const today = this.dailyDeclarationService.checkResult()?.today;
        if (!today) return;
        this.weekDays.update(days => days.map(day =>
            day.date === today ? { ...day, ticketRestaurant: checked } : day
        ));
    }

    onTicketRestaurantInput(date: string, event: Event): void {
        const target = event.target;
        if (!(target instanceof HTMLInputElement)) return;
        this.weekDays.update(days => days.map(day =>
            day.date === date ? { ...day, ticketRestaurant: target.checked } : day
        ));
    }

    onCommentInputForDate(date: string, event: Event): void {
        const target = event.target;
        if (!(target instanceof HTMLInputElement)) return;
        this.weekDays.update(days => days.map(day =>
            day.date === date ? { ...day, comment: target.value } : day
        ));
    }

    shouldShowTicketRestaurantFor(day: WeekDayDeclaration): boolean {
        return this.ticketsRestaurantEnabled() && this.shouldReceiveTicketRestaurant(day);
    }

    async submit(): Promise<void> {
        if (!this.canSubmit()) return;

        this.isSaving.set(true);
        try {
            // Le commentaire du formulaire réactif ne s'applique qu'au jour courant.
            const today = this.dailyDeclarationService.checkResult()?.today;
            if (today) {
                this.weekDays.update(days => days.map(day =>
                    day.date === today ? { ...day, comment: (this.commentForm.controls['comment'].value || '').trim() } : day
                ));
            }

            await this.persistWeek();
            this.toastrService.success('Déclaration du jour enregistrée avec succès');
            this.dailyDeclarationService.dismissForToday();
            this.modalService.close(this.modalId);
        } catch (error) {
            console.error('Erreur lors de l’enregistrement:', error);
            this.toastrService.error('Erreur lors de l’enregistrement de la déclaration');
        } finally {
            this.isSaving.set(false);
        }
    }

    private async loadWeek(today: string): Promise<void> {
        const loadSequence = ++this.weekLoadSequence;
        const weekDates = this.getWorkingWeekDates(this.parseDate(today));
        const monthKeys = [...new Set(weekDates.map(date => this.getMonthKey(date)))];
        const contactId = this.authService.selectedContact();

        this.isLoadingWeek.set(true);
        try {
            const responses = await Promise.all(monthKeys.map(month =>
                firstValueFrom(this.declarationMensuelleService.getBy(
                    { contact: contactId, month },
                    undefined,
                    true
                ) as import('rxjs').Observable<MonthlyDeclaration[]>)
            ));
            if (loadSequence !== this.weekLoadSequence) return;

            this.declarationByMonth = new Map(
                responses.flat().map(declaration => [this.getMonthKeyFromDeclaration(declaration), declaration])
            );
            const prefillData = this.dailyDeclarationService.checkResult()?.prefillData;

            this.weekDays.set(weekDates.map(date => {
                const dateKey = this.formatDate(date);
                const declaration = this.declarationByMonth.get(this.getMonthKey(date));
                const savedDay = declaration?.declaration.find(day => day.date === dateKey);
                const fallback = dateKey === today ? prefillData : undefined;

                return {
                    date: dateKey,
                    label: date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'short' }),
                    morning: savedDay?.morning ?? fallback?.morning ?? '',
                    afternoon: savedDay?.afternoon ?? fallback?.afternoon ?? '',
                    ticketRestaurant: savedDay?.ticketRestaurant ?? fallback?.ticketRestaurant ?? false,
                    comment: savedDay?.comment ?? fallback?.comment ?? '',
                };
            }));

            this.commentForm.controls['comment'].setValue(
                this.weekDays().find(d => d.date === today)?.comment || ''
            );
        } catch (error) {
            console.error('Erreur lors du chargement des déclarations de la semaine:', error);
            this.toastrService.error('Impossible de charger les déclarations de la semaine');
        } finally {
            if (loadSequence === this.weekLoadSequence) {
                this.isLoadingWeek.set(false);
            }
        }
    }

    private async persistWeek(): Promise<void> {
        const contactId = this.authService.selectedContact();
        const daysByMonth = new Map<string, WeekDayDeclaration[]>();

        for (const day of this.weekDays()) {
            const month = day.date.slice(0, 7);
            daysByMonth.set(month, [...(daysByMonth.get(month) ?? []), day]);
        }

        await Promise.all([...daysByMonth.entries()].map(async ([month, days]) => {
            const existingDeclaration = this.declarationByMonth.get(month);
            const declaration = this.mergeWeekIntoMonth(month, days, existingDeclaration?.declaration ?? []);

            if (existingDeclaration) {
                await firstValueFrom(this.apiService.patch(
                    this.declarationMensuelleService.API_URL,
                    existingDeclaration.id,
                    { declaration }
                ));
                return;
            }

            await firstValueFrom(this.apiService.post(this.declarationMensuelleService.API_URL, {
                contact: contactId,
                mois: `${month}-01`,
                declaration,
            }));
        }));
    }

    private mergeWeekIntoMonth(
        month: string,
        weekDays: WeekDayDeclaration[],
        savedDays: StoredDayDeclaration[]
    ): StoredDayDeclaration[] {
        const updatesByDate = new Map(weekDays.map(day => [day.date, day]));
        const baseDays = savedDays.length > 0
            ? savedDays
            : this.createEmptyMonthDays(month);

        // Seul le jour courant est « confirmé ». Les autres jours de la semaine sont
        // remplis d'avance depuis la vue Semaine : ils sont bien enregistrés, mais leur
        // `validated` n'est pas touché.
        //
        // Les marquer aussi, ce que faisait la version précédente, supprimait la modale
        // les jours suivants : le serveur y voyait une journée déjà confirmée et ne la
        // reproposait plus. Quiconque remplissait sa semaine perdait donc son rappel
        // quotidien pour le reste de la semaine — alors que `validated` doit dire
        // « j'ai confirmé ce jour-là », pas « ce jour-là est renseigné ».
        const confirmedDate = this.dailyDeclarationService.checkResult()?.today;

        return baseDays.map(savedDay => {
            const update = updatesByDate.get(savedDay.date);
            if (!update) return savedDay;

            return {
                ...savedDay,
                date: update.date,
                morning: update.morning,
                afternoon: update.afternoon,
                ticketRestaurant: update.ticketRestaurant,
                comment: update.comment,
                validated: savedDay.date === confirmedDate
                    ? !!update.morning && !!update.afternoon
                    : !!savedDay.validated,
            };
        });
    }

    private createEmptyMonthDays(month: string): StoredDayDeclaration[] {
        const [year, monthNumber] = month.split('-').map(Number);
        const daysInMonth = new Date(year, monthNumber, 0).getDate();

        return Array.from({ length: daysInMonth }, (_, index) => {
            const date = new Date(year, monthNumber - 1, index + 1);
            const isWeekend = date.getDay() === 0 || date.getDay() === 6;
            return {
                date: this.formatDate(date),
                morning: isWeekend ? 'week-end' : '',
                afternoon: isWeekend ? 'week-end' : '',
                ticketRestaurant: false,
                comment: '',
                managerValidation: false,
                managerComment: '',
            };
        });
    }

    private getWorkingWeekDates(today: Date): Date[] {
        const monday = new Date(today);
        const dayOfWeek = monday.getDay() || 7;
        monday.setDate(monday.getDate() - dayOfWeek + 1);

        return Array.from({ length: 5 }, (_, index) => {
            const date = new Date(monday);
            date.setDate(monday.getDate() + index);
            return date;
        });
    }

    private shouldReceiveTicketRestaurant(day: Pick<WeekDayDeclaration, 'morning' | 'afternoon'>): boolean {
        const workActivities = new Set(['sur-site', 'teletravail', 'formation', 'rdv-client']);
        return workActivities.has(day.morning) && workActivities.has(day.afternoon);
    }

    private getMonthKey(date: Date): string {
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    }

    private getMonthKeyFromDeclaration(declaration: MonthlyDeclaration): string {
        const firstDay = declaration.declaration[0]?.date;
        return firstDay?.slice(0, 7) ?? declaration.mois?.slice(0, 7) ?? this.getMonthKey(this.todayDate());
    }

    private parseDate(value: string): Date {
        const [year, month, day] = value.split('-').map(Number);
        return new Date(year, month - 1, day);
    }

    private formatDate(date: Date): string {
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    }
}
